const SVG_NS = 'http://www.w3.org/2000/svg';
const XLINK_NS = 'http://www.w3.org/1999/xlink';
const XHTML_NS = 'http://www.w3.org/1999/xhtml';

// Presentation properties that carry the map's SCSS styling. We copy these onto
// the clone because computed styles from external stylesheets are lost once the
// SVG is serialised and rasterised in isolation.
const STYLE_PROPS = [
  'fill',
  'fill-opacity',
  'fill-rule',
  'stroke',
  'stroke-width',
  'stroke-opacity',
  'stroke-linecap',
  'stroke-linejoin',
  'stroke-dasharray',
  'stroke-dashoffset',
  'vector-effect',
  'opacity',
  'color',
  'visibility',
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'text-anchor',
  'dominant-baseline',
  'letter-spacing',
  'shape-rendering',
  'paint-order',
];

// CSS-initial values for the paint properties. The flows are drawn as
// `<use xlink:href="#flow" fill="url(#gradient)">` where the referenced template
// path has no fill of its own. If we inline the template's *computed* fill (the
// initial black) it becomes an explicit fill that overrides the gradient the
// <use> is trying to apply — turning every flow solid black. The same initial
// black on the templates' <defs> ancestors leaks in the same way.
//
// stroke-width behaves identically: the arrow template `<path id="arrow-…">` lives
// in <defs> with no width of its own and inherits 0.5px from the
// `<use class="map__flow__arrow">` that references it. Its *computed* width in the
// <defs> context is the initial 1px, so inlining it would override the inherited
// 0.5px and draw arrows at double thickness.
//
// Skipping the initial value is a no-op for directly-rendered elements (they'd
// paint with the initial value anyway) but preserves inheritance for the <use>
// templates.
const INITIAL_PAINT = { fill: 'rgb(0, 0, 0)', stroke: 'none', 'stroke-width': '1px' };

function fetchAsDataUri(url) {
  return fetch(url)
    .then((response) => response.blob())
    .then(
      (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        })
    );
}

function inlineStyles(source, clone) {
  const sourceNodes = [source, ...source.querySelectorAll('*')];
  const cloneNodes = [clone, ...clone.querySelectorAll('*')];

  for (let i = 0; i < sourceNodes.length; i++) {
    const computed = window.getComputedStyle(sourceNodes[i]);
    const cloneNode = cloneNodes[i];
    let cssText = '';

    STYLE_PROPS.forEach((prop) => {
      const value = computed.getPropertyValue(prop);

      // Skip url() references (flow gradients) so the presentation attribute keeps
      // resolving against the in-document <defs>, rather than an absolutised URL
      // that won't exist in the standalone image.
      if (!value || value.indexOf('url(') !== -1) {
        return;
      }

      if (INITIAL_PAINT[prop] === value) {
        return;
      }

      cssText += `${prop}:${value};`;
    });

    if (cssText) {
      const existing = cloneNode.getAttribute('style') || '';
      cloneNode.setAttribute('style', existing + cssText);
    }

    // Gradient stop colours come from CSS, and the <img> rasteriser honours
    // stop-color/stop-opacity only as presentation attributes, not via style.
    if (sourceNodes[i].tagName.toLowerCase() === 'stop') {
      cloneNode.setAttribute('stop-color', computed.getPropertyValue('stop-color'));
      cloneNode.setAttribute('stop-opacity', computed.getPropertyValue('stop-opacity'));
    }
  }
}

// The map background is a bundled <image>. External refs aren't loaded when an SVG
// is rasterised via an <img>, and would taint the canvas if they were, so inline it.
function inlineImages(clone) {
  const images = Array.from(clone.querySelectorAll('image'));

  return Promise.all(
    images.map((image) => {
      const href = image.getAttribute('href') || image.getAttributeNS(XLINK_NS, 'href');

      if (!href || href.indexOf('data:') === 0) {
        return Promise.resolve();
      }

      return fetchAsDataUri(href)
        .then((dataUri) => {
          image.setAttribute('href', dataUri);
          image.setAttributeNS(XLINK_NS, 'href', dataUri);
        })
        .catch(() => {
          // Leave the original href; the background may just be missing.
        });
    })
  );
}

// Best-effort embedding of the matched web font(s) as data URIs. Without this,
// text falls back to a system font when rasterised. Never blocks the export.
export function buildFontFaceCss(familyPattern) {
  const faces = [];

  Array.from(document.styleSheets).forEach((sheet) => {
    let rules;

    try {
      rules = sheet.cssRules;
    } catch (error) {
      // Cross-origin stylesheet — not readable.
      return;
    }

    if (!rules) {
      return;
    }

    Array.from(rules).forEach((rule) => {
      const isFontFace = rule.type === CSSRule.FONT_FACE_RULE;

      if (isFontFace && familyPattern.test(rule.style.getPropertyValue('font-family'))) {
        faces.push(rule.cssText);
      }
    });
  });

  return Promise.all(
    faces.map(async (cssText) => {
      // Inline only the first source per face. It's the woff2 (listed first in
      // every declaration), which the rasteriser will use; fetching the woff/ttf
      // fallbacks too would just bloat the data URI with formats never drawn.
      const match = cssText.match(/url\(["']?([^"')]+)["']?\)/);
      const url = match && match[1];

      if (!url || url.indexOf('data:') === 0) {
        return cssText;
      }

      try {
        const dataUri = await fetchAsDataUri(url);
        return cssText.split(url).join(dataUri);
      } catch (error) {
        return cssText;
      }
    })
  ).then((parts) => parts.join('\n'));
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = filename;

  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  URL.revokeObjectURL(url);
}

// Clone an SVG element into a standalone, self-contained copy: computed styles
// inlined, background <image> embedded as a data URI, and the web font embedded.
// Returns the clone plus its intrinsic size. Shared by canvas rasterisation and
// SVG serialisation.
//
// Prefer the viewBox over the rendered rect so the output is the map's intrinsic
// size regardless of how the SVG is scaled on screen. On mobile the map is laid
// out at its 748-wide viewBox but CSS-scaled down to ~390; using the rect would
// give a tiny image, whereas the viewBox gives the full-size map.
async function inlineSvgClone(svg, { fontCss }) {
  const viewBox = svg.viewBox && svg.viewBox.baseVal;
  const rect = svg.getBoundingClientRect();
  const width = Math.round(viewBox && viewBox.width ? viewBox.width : rect.width);
  const height = Math.round(viewBox && viewBox.height ? viewBox.height : rect.height);

  const clone = svg.cloneNode(true);
  clone.setAttribute('xmlns', SVG_NS);
  clone.setAttribute('xmlns:xlink', XLINK_NS);
  clone.setAttribute('width', width);
  clone.setAttribute('height', height);

  inlineStyles(svg, clone);

  await inlineImages(clone);

  if (fontCss) {
    const styleEl = document.createElementNS(SVG_NS, 'style');
    styleEl.textContent = fontCss;
    clone.insertBefore(styleEl, clone.firstChild);
  }

  return { clone, width, height };
}

// Rasterise an SVG element to a canvas at `scale`. When `background` is null the
// canvas is left transparent (used for the legend, which sits over the map).
// `fontCss` is the pre-built @font-face CSS (embedded so text keeps its web font).
async function svgToCanvas(svg, { scale, fontCss, background }) {
  const { clone, width, height } = await inlineSvgClone(svg, { fontCss });

  const svgString = new XMLSerializer().serializeToString(clone);
  const image = await loadImage(
    `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`
  );

  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;

  const ctx = canvas.getContext('2d');
  if (background) {
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

  return canvas;
}

// Serialise a live SVG element to a standalone SVG string, with everything inlined
// so it renders on its own. When `background` is set a filled rect is inserted
// behind the content — an SVG has no canvas-style backdrop of its own.
export async function svgToString(svg, { fontCss, background } = {}) {
  const { clone, width, height } = await inlineSvgClone(svg, { fontCss });

  if (background) {
    const rectEl = document.createElementNS(SVG_NS, 'rect');
    rectEl.setAttribute('x', '0');
    rectEl.setAttribute('y', '0');
    rectEl.setAttribute('width', width);
    rectEl.setAttribute('height', height);
    rectEl.setAttribute('fill', background);
    clone.insertBefore(rectEl, clone.firstChild);
  }

  return new XMLSerializer().serializeToString(clone);
}

// Rasterise an HTML element (e.g. the policies marker legend) to a transparent
// canvas via <foreignObject>. The rasteriser only honours HTML inside a foreign
// object when the root carries the XHTML namespace, and — as for SVG — external
// stylesheet rules are lost, so every computed style is inlined onto the clone.
async function htmlToCanvas(el, { scale, fontCss }) {
  const rect = el.getBoundingClientRect();
  const width = Math.ceil(rect.width);
  const height = Math.ceil(rect.height);

  const sourceNodes = [el, ...el.querySelectorAll('*')];
  const clone = el.cloneNode(true);
  const cloneNodes = [clone, ...clone.querySelectorAll('*')];

  for (let i = 0; i < sourceNodes.length; i++) {
    const computed = window.getComputedStyle(sourceNodes[i]);
    let cssText = '';
    for (let j = 0; j < computed.length; j++) {
      const prop = computed[j];
      cssText += `${prop}:${computed.getPropertyValue(prop)};`;
    }
    cloneNodes[i].setAttribute('style', cssText);
  }

  clone.setAttribute('xmlns', XHTML_NS);

  const style = fontCss ? `<style>${fontCss}</style>` : '';
  const svgString =
    `<svg xmlns="${SVG_NS}" width="${width}" height="${height}">${style}` +
    `<foreignObject x="0" y="0" width="100%" height="100%">` +
    `${new XMLSerializer().serializeToString(clone)}</foreignObject></svg>`;

  const image = await loadImage(
    `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`
  );

  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;
  canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);

  return canvas;
}

// Rasterise a DOM element to a canvas: an <svg> directly, any other element via
// <foreignObject>. `background` (SVG only) fills the canvas; omit it — or pass null —
// for a transparent result (e.g. an overlay). `fontCss` is embedded so text keeps its
// web font; build it once with buildFontFaceCss and share it across calls.
export function elementToCanvas(el, options) {
  if (el.tagName.toLowerCase() === 'svg') {
    return svgToCanvas(el, { background: null, ...options });
  }
  return htmlToCanvas(el, options);
}

// Encode a canvas to a PNG and trigger a browser download.
export async function downloadCanvasAsPng(canvas, filename) {
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((result) => {
      if (result) {
        resolve(result);
      } else {
        reject(new Error('Failed to generate PNG'));
      }
    }, 'image/png');
  });

  triggerDownload(blob, filename);
}

// Wrap an SVG string in a blob and trigger a browser download.
export function downloadSvg(svgString, filename) {
  const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  triggerDownload(blob, filename);
}
